import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Estado } from '../../componentes/estados';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, nomePessoa, textoLivre } from '../../nucleo/formato';
import { caminhoDaTarefa } from './crm-caminhos';
import { IconeTipoAtividade } from './tarefas-comum';
import type { Oportunidade, TipoAtividade } from './tipos';

/**
 * Lista de 30/10 (B), item 36: filtro "Excluídas" da aba Tarefas da negociação. Mostra as tarefas excluídas DESTA negociação
 * que estão na Lixeira (quem excluiu, quando e a justificativa do item 13) e restaura cada uma ali mesmo pela rota da Lixeira
 * (`POST /lixeira/pipeline.atividade/:id/restaurar`): mesmas permissões e regras (o servidor diz `pode_restaurar`).
 */

export interface TarefaExcluida {
  id: string;
  entidade: string;
  rotulo: string | null;
  quando: string | null;
  excluido_em: string;
  restaurar_ate: string;
  ator_nome: string | null;
  assistente_nome: string | null;
  pode_restaurar: boolean;
  tipo: TipoAtividade;
  estado: 'pendente' | 'concluida' | 'cancelada';
  justificativa: string | null;
  /** Lista de 02/11 (itens 8 e 9): a consulta geral traz a negociação e o cliente (modo discreto). */
  parceiro_nome?: string | null;
  oportunidade_id?: string;
  oportunidade_titulo?: string;
}

const ENTIDADE = 'pipeline.oportunidade';

export const chaveTarefasExcluidas = (id: string) => ['pipeline', 'atividades-excluidas', id] as const;

/**
 * Lista de 02/11 (itens 7, 8 e 9): restaurar uma tarefa excluída (rota da Lixeira), igual nas três telas (aba Tarefas da
 * negociação, página Atividades e pop-up Tarefas e reuniões). Depois, invalida as listas das três, a Lixeira e o histórico da
 * negociação dona.
 */
export function useRestaurarTarefaExcluida(oportunidadeId?: string) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (i: TarefaExcluida) => post(`/lixeira/pipeline.atividade/${i.id}/restaurar`),
    onSuccess: async (_r, i) => {
      toast.sucesso(t('crm.excluidas.restaurada'));
      // A tarefa volta à lista da negociação, sai da Lixeira e o histórico ganha "Atividade restaurada da lixeira".
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
      await qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] });
      await qc.invalidateQueries({ queryKey: ['/agenda/agendamentos'] });
      await qc.invalidateQueries({ queryKey: ['chatter', ENTIDADE, i.oportunidade_id ?? oportunidadeId] });
      await qc.invalidateQueries({ queryKey: ['lixeira'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
}

/**
 * A linha da tarefa excluída: tipo, texto, quem excluiu, quando, justificativa, prazo, selo "Excluída", data da tarefa e
 * Restaurar (só com `pode_restaurar`, que o servidor calcula pela mesma permissão da rota). Com `comNegociacao`, mostra também
 * a negociação (links para a ficha) quando a lista não é de uma negociação só.
 */
export function LinhaTarefaExcluida({ i, parceiro, restaurar, comNegociacao, aoNavegar }: { i: TarefaExcluida; parceiro: string | null | undefined; restaurar: UseMutationResult<unknown, Error, TarefaExcluida>; comNegociacao?: boolean; aoNavegar?: () => void }) {
  const { t } = useTranslation('pipeline');
  const dono = parceiro ?? i.parceiro_nome ?? null;
  const titulo = textoLivre(i.rotulo ?? '', dono);
  const por = i.ator_nome ? ` ${i.assistente_nome ? t('crm.excluidas.porAssistente', { assistente: i.assistente_nome, nome: nomePessoa(i.ator_nome) }) : t('crm.excluidas.por', { nome: nomePessoa(i.ator_nome) })}` : '';
  const negociacao = comNegociacao && i.oportunidade_id ? textoLivre(i.oportunidade_titulo ?? '', dono) : '';
  return (
    <li className="@container flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm" data-tarefa-excluida={i.id}>
      <span className="inline-flex items-center gap-1 text-xs text-tinta-3" title={t(`tipoAtividade.${i.tipo}`)}>
        <IconeTipoAtividade tipo={i.tipo} />
        <span>{t(`tipoAtividade.${i.tipo}`)}</span>
      </span>
      {/* rlb-5 VIS-M1 e rl0211-7 VIS-M1: em lista estreita (celular, gaveta do pop-up) o texto vem primeiro e ocupa a linha inteira;
          tipo, selo, data e "Restaurar" quebram para baixo. Decide pela largura da lista, não da tela (só visual: nada focável muda de ordem). */}
      <div className="order-first w-full min-w-0 @2xl:order-none @2xl:w-auto @2xl:flex-1" data-texto-excluida="">
        <TextoCortado como="p" className="font-medium">
          {titulo}
        </TextoCortado>
        {negociacao && i.oportunidade_id && (
          <Link to={caminhoDaTarefa(i.oportunidade_id)} onClick={aoNavegar} title={negociacao} className="block truncate text-xs text-acento-forte hover:underline" data-excluida-negociacao="">
            {negociacao}
          </Link>
        )}
        <p className="text-xs text-tinta-3" data-excluida-por="">
          {t('crm.excluidas.excluidaEm', { data: dataHora(i.excluido_em) })}
          {por}
        </p>
        {i.justificativa && (
          <p className="whitespace-pre-wrap break-words text-xs text-tinta-2" data-justificativa-exclusao="">
            {t('crm.excluidas.justificativa', { texto: textoLivre(i.justificativa, dono) })}
          </p>
        )}
        <p className="text-xs text-tinta-3">{t('crm.excluidas.ate', { data: data(i.restaurar_ate) })}</p>
      </div>
      <Selo cor="neutro">{t('crm.excluidas.selo')}</Selo>
      {i.quando && <span className="numero text-xs text-tinta-2">{dataHora(i.quando)}</span>}
      {i.pode_restaurar && (
        <Botao
          tamanho="sm"
          icone={<RotateCcw className="size-4" aria-hidden />}
          aria-label={t('crm.excluidas.restaurarTarefa', { titulo })}
          carregando={restaurar.isPending && restaurar.variables?.id === i.id}
          disabled={restaurar.isPending}
          onClick={() => restaurar.mutate(i)}
          data-restaurar-tarefa={i.id}
        >
          {t('crm.excluidas.restaurar')}
        </Botao>
      )}
    </li>
  );
}

export function TarefasExcluidas({ op }: { op: Oportunidade }) {
  const { t } = useTranslation('pipeline');
  const q = useExcluidasDaNegociacao(op.id, true);
  const restaurar = useRestaurarTarefaExcluida(op.id);
  return (
    <Estado consulta={q}>
      {(d) =>
        d.itens.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-borda px-4 py-6 text-center text-sm text-tinta-3" data-tarefas-excluidas-vazio="">
            <p>{t('crm.excluidas.vazio')}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <ul aria-label={t('crm.excluidas.lista')} className="divide-y divide-borda rounded-xl border border-borda bg-sup-1" data-tarefas-excluidas="">
              {d.itens.map((i) => (
                <LinhaTarefaExcluida key={i.id} i={i} parceiro={op.parceiro_nome} restaurar={restaurar} />
              ))}
            </ul>
            {d.truncado && <p className="text-xs text-tinta-3">{t('crm.excluidas.truncado', { n: d.itens.length })}</p>}
          </div>
        )
      }
    </Estado>
  );
}

/** As excluídas desta negociação (a mesma consulta para o filtro "Excluídas" e para "Todas"; `ativo` liga a busca). */
export function useExcluidasDaNegociacao(id: string, ativo: boolean) {
  return useQuery({
    queryKey: chaveTarefasExcluidas(id),
    queryFn: () => get<{ itens: TarefaExcluida[]; truncado: boolean }>(`/pipeline/oportunidades/${id}/atividades/excluidas`),
    enabled: ativo,
  });
}

/**
 * Lista de 02/11 (itens 8 e 9): as tarefas excluídas de todas as negociações (`GET /pipeline/atividades/excluidas`), com os
 * mesmos filtros da página Atividades (`params`: tipos, responsaveis, de, ate, busca...), em páginas ("Carregar mais"). Usada
 * no filtro "Excluídas" e em "Todos os status" da página e no pop-up Tarefas e reuniões (minhas, do dia). A chave começa por
 * `/pipeline/atividades`: toda escrita das três telas já a invalida.
 */
export function TarefasExcluidasGeral({ params, vazio, titulo, rotuloLista, aoNavegar }: { params: Record<string, unknown>; vazio: string; titulo?: string; rotuloLista?: string; aoNavegar?: () => void }) {
  const { t } = useTranslation('pipeline');
  const restaurar = useRestaurarTarefaExcluida();
  const q = useInfiniteQuery({
    queryKey: ['/pipeline/atividades', 'excluidas', params],
    queryFn: ({ pageParam }) => get<{ itens: TarefaExcluida[]; proximo_cursor: string | null }>('/pipeline/atividades/excluidas', { ...params, limite: 50, cursor: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (u) => u.proximo_cursor ?? undefined,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  if (q.isPending) return <Carregando texto={t('crm.excluidas.geralCarregando')} />;
  // rl0211-8 (ROB-B1): falhar a página seguinte ou uma recarga não tira da tela o que já veio.
  if (q.isError && !q.data) return <Erro erro={q.error} aoTentar={() => void q.refetch()} />;
  return (
    <section className="flex flex-col gap-2" data-tarefas-excluidas-geral="">
      {titulo && <h2 className="text-sm font-semibold text-tinta-2">{titulo}</h2>}
      {itens.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-borda px-4 py-6 text-center text-sm text-tinta-3" data-tarefas-excluidas-vazio="">
          <p>{vazio}</p>
        </div>
      ) : (
        <ul aria-label={rotuloLista ?? t('crm.excluidas.geralLista')} className="divide-y divide-borda rounded-xl border border-borda bg-sup-1" data-tarefas-excluidas="">
          {itens.map((i) => (
            <LinhaTarefaExcluida key={i.id} i={i} parceiro={i.parceiro_nome} restaurar={restaurar} comNegociacao aoNavegar={aoNavegar} />
          ))}
        </ul>
      )}
      {q.isError && (
        <p role="alert" className="text-center text-xs text-perigo" data-erro-excluidas-mais="">
          {mensagemDeErro(q.error)}
        </p>
      )}
      {q.hasNextPage && (
        <div className="flex justify-center">
          <Botao variante="secundario" tamanho="sm" carregando={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()} data-carregar-mais-excluidas="">
            {t('crm.excluidas.carregarMais')}
          </Botao>
        </div>
      )}
    </section>
  );
}
